import React, { useState, useEffect } from 'react';
import GavLogo1 from '../assets/GavLogo1.png';
import GavLogo2 from '../assets/GavLogo2.png';
import linkedin from '../assets/linkedin.webp';
import leetcode from '../assets/leetcode.png';

export default function Header() {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 750);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 750);
      if (window.innerWidth > 750) {
        setMenuOpen(false);
      }
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    // Initial scroll position
    handleScroll();
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu when clicking outside on mobile
  useEffect(() => {
    if (!menuOpen || !isSmallScreen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the menu and hamburger button
      const menuElement = document.querySelector('[data-mobile-menu]');
      const hamburgerElement = document.querySelector('[data-hamburger-menu]');
      
      if (menuElement && hamburgerElement) {
        const isClickInsideMenu = menuElement.contains(target);
        const isClickOnHamburger = hamburgerElement.contains(target);
        
        if (!isClickInsideMenu && !isClickOnHamburger) {
          setMenuOpen(false);
        }
      }
    };

    // Add a small delay to prevent immediate closing when opening the menu
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [menuOpen, isSmallScreen]);

  const scrollToContact = () => {
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    setMenuOpen(false);
    // Dispatch custom event to trigger shake animation
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('contactScroll'));
    }, 800); // Delay to allow scroll to complete
  };

  const handleResumeClick = () => {
    window.open('https://github.com/hashedonemilliontimes/Resume/blob/main/Gavin%20Milligan-Resume.pdf', '_blank');
    setMenuOpen(false);
  };

  // Calculate gradual transition over 500px scroll range for smoother transition
  const transitionRange = 500;
  const logo2Opacity = Math.max(0, Math.min(1, 1 - (scrollY / transitionRange)));
  const logo1Opacity = Math.max(0, Math.min(1, scrollY / transitionRange));
  
  const isScrolled = scrollY > 0;

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      width: '100%',
      maxWidth: '100vw',
      padding: isSmallScreen ? '15px 20px' : '15px 40px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 10000,
      backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
      backdropFilter: isScrolled ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isScrolled ? 'blur(10px)' : 'none',
      boxShadow: isScrolled ? '0px 2px 4px rgba(0, 0, 0, 0.05)' : 'none',
      boxSizing: 'border-box',
      transition: 'background-color 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease',
      transform: 'translateZ(0)',
      WebkitTransform: 'translateZ(0)',
      willChange: 'transform'
    }}>
      {/* Logo */}
      <div style={{ 
        display: 'inline-block',
        flexShrink: 0,
        position: 'relative',
        height: isSmallScreen ? '40px' : '50px',
        cursor: 'pointer'
      }}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        {/* Invisible spacer to maintain container width and preload */}
        <img
          src={GavLogo1}
          alt=""
          style={{
            height: isSmallScreen ? '40px' : '50px',
            width: 'auto',
            maxHeight: '50px',
            opacity: 0,
            pointerEvents: 'none',
            display: 'block'
          }}
        />
        {/* GavLogo2 - fades out as user scrolls down */}
        <img
          src={GavLogo2}
          alt="Gavin Milligan Logo"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: isSmallScreen ? '40px' : '50px',
            width: 'auto',
            maxHeight: '50px',
            opacity: logo2Opacity,
            pointerEvents: 'none'
          }}
        />
        {/* GavLogo1 - fades in as user scrolls down */}
        <img
          src={GavLogo1}
          alt="Gavin Milligan Logo"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: isSmallScreen ? '40px' : '50px',
            width: 'auto',
            maxHeight: '50px',
            opacity: logo1Opacity,
            pointerEvents: 'none'
          }}
        />
      </div>

      {/* Navigation */}
      {isSmallScreen ? (
        <>
          {/* Hamburger Menu Icon */}
          <div
            data-hamburger-menu
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: '5px',
              padding: '5px',
              flexShrink: 0,
              marginLeft: 'auto'
            }}
          >
            <div style={{
              width: '25px',
              height: '3px',
              backgroundColor: '#333333',
              borderRadius: '2px'
            }}></div>
            <div style={{
              width: '25px',
              height: '3px',
              backgroundColor: '#333333',
              borderRadius: '2px'
            }}></div>
            <div style={{
              width: '25px',
              height: '3px',
              backgroundColor: '#333333',
              borderRadius: '2px'
            }}></div>
          </div>

          {/* Mobile Menu Dropdown */}
          {menuOpen && (
            <div 
              data-mobile-menu
              style={{
                position: 'fixed',
                top: '80px',
                right: '20px',
                backgroundColor: 'white',
                borderRadius: '10px',
                boxShadow: '0px 4px 6px rgba(0, 0.1, 0.1, 0.5)',
                padding: '20px',
                minWidth: '150px',
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                zIndex: 1001
              }}
            >
              <div
                onClick={handleResumeClick}
                style={{
                  color: '#333333',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                Résumé
              </div>
              <div
                onClick={scrollToContact}
                style={{
                  color: '#333333',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
              >
                Contact
              </div>
              <a
                href="https://leetcode.com/u/gavinm01/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textDecoration: 'none',
                  color: '#333333',
                  fontSize: '16px',
                  padding: '5px'
                }}
              >
                <img
                  src={leetcode}
                  alt="LeetCode"
                  style={{
                    width: '24px',
                    height: 'auto',
                    filter: 'invert(58%) brightness(50%)'
                  }}
                />
                LeetCode
              </a>
              <a
                href="https://www.linkedin.com/in/gavin-d-milligan/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  textDecoration: 'none',
                  color: '#333333',
                  fontSize: '16px',
                  padding: '5px'
                }}
              >
                <img
                  src={linkedin}
                  alt="LinkedIn"
                  style={{
                    width: '24px',
                    height: 'auto',
                    filter: 'invert(58%) brightness(50%)'
                  }}
                />
                LinkedIn
              </a>
            </div>
          )}
        </>
      ) : (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          marginRight: '0',
          flexShrink: 0
        }}>
          <div
            onClick={handleResumeClick}
            style={{
              color: '#333333',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
          >
            Résumé
          </div>
          <div
            onClick={scrollToContact}
            style={{
              color: '#333333',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
          >
            Contact
          </div>
          <a
            href="https://leetcode.com/u/gavinm01/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <img
              src={leetcode}
              alt="LeetCode"
              style={{
                width: '28px',
                height: 'auto',
                cursor: 'pointer',
                filter: 'invert(58%) brightness(50%)'
              }}
            />
          </a>
          <a
            href="https://www.linkedin.com/in/gavin-d-milligan/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            <img
              src={linkedin}
              alt="LinkedIn"
              style={{
                width: '28px',
                height: 'auto',
                cursor: 'pointer',
                filter: 'invert(58%) brightness(50%)'
              }}
            />
          </a>
        </div>
      )}
    </header>
  );
}

